import { BotoesNavegacao } from '@/global/components/BotoesNavegacao'
import { DateField } from '@/global/components/DateField'
import { TextField } from '@/global/components/TextField'
import { realceCampoAberto } from '@/global/components/ui/input'
import { Calendar } from '@/global/components/ui/calendar'
import { MonthCalendar } from '@/global/components/ui/month-calendar'
import { cn } from '@/lib/utils'
import { Empresa } from '@/service/api'
import { PeriodoPontoFactory, Void } from '@acme/common'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import React, { createContext, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useStore } from 'store'
import { match } from 'ts-pattern'
import Menu from '../Menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'

type TValue = [string, string]

type Tipo = 'competencia' | 'data' | 'periodo'
export type { Tipo as TipoPeriodo }

const TIPOS: Tipo[] = ['competencia', 'data', 'periodo']

const LABEL_TIPO: Record<Tipo, string> = {
    competencia: 'Competência',
    data: 'Data',
    periodo: 'Período',
}

type EmpresaPeriodo = Pick<Empresa, 'diaInicioPonto' | 'TrocaInicioPonto'>

const EMPRESA_FALLBACK: EmpresaPeriodo = {
    diaInicioPonto: 1,
    TrocaInicioPonto: [],
}

function trocarTipo(
    tipo: Tipo,
    dataFinal: string,
    empresa: EmpresaPeriodo,
    handleChange: IRootProps['onChange'],
    changeType: (t: Tipo) => void,
) {
    switch (tipo) {
        case 'competencia':
        case 'periodo': {
            const [diaInicial, diaFinal] = PeriodoPontoFactory.fromCompetencia(
                dayjs(dataFinal).format('YYYY-MM'),
                {
                    diaInicioPonto: empresa.diaInicioPonto,
                    trocasInicioPonto: empresa.TrocaInicioPonto,
                },
            ).getPeriodo(false)
            handleChange([
                diaInicial.format('YYYY-MM-DD'),
                diaFinal.format('YYYY-MM-DD'),
            ])
            break
        }
        case 'data': {
            const dia = dayjs().isBefore(dataFinal)
                ? dayjs().format('YYYY-MM-DD')
                : dataFinal
            handleChange([dia, dia])
            break
        }
    }
    changeType(tipo)
}

const Context = createContext({
    state: [
        dayjs().format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
    ] as TValue,
    allowedTypes: ['data'] as Tipo[],
    type: 'data' as Tipo,
    changeType: (t: Tipo) => {
        console.debug('changeType', t)
    },
    label: 'Período' as string | undefined,
    handlePrev: Void,
    handleNext: Void,
    handleChange: Void as IRootProps['onChange'],
    empresa: EMPRESA_FALLBACK as EmpresaPeriodo,
})

interface IRootProps {
    label?: string
    allowedTypes?: Tipo[]
    value: TValue
    onChange: React.Dispatch<React.SetStateAction<TValue>>
    children: React.ReactNode
    /** com botões: `320`, sem botões: `220`
     * @default 220
     */
    width?: number | string
    type?: Tipo
    setType?: React.Dispatch<React.SetStateAction<Tipo>>
    /** empresa usada pro cálculo de competência — quando ausente, cai pra `state.company` do store e, na falta dos dois, pra um fallback sintético (mês calendário, sem trocas) */
    empresa?: EmpresaPeriodo | null
    /** Classe do elemento raiz. */
    className?: string
}

function Root({
    label,
    onChange,
    value,
    allowedTypes = ['competencia'],
    children,
    width = 220,
    type: _type,
    setType: _setType,
    empresa: empresaProp,
    className,
}: IRootProps) {
    const [typeDisplayed, setTypeDisplayed] = useState<Tipo>(allowedTypes[0])
    const storeEmpresa = useStore(state => state.company)
    const empresa = empresaProp ?? storeEmpresa ?? EMPRESA_FALLBACK
    const td = _type ?? typeDisplayed
    const setTd = _setType ?? setTypeDisplayed

    const setStore = useStore(state => state.setCompetencia)

    useEffect(() => {
        if (td === 'competencia') {
            const [competencia] = PeriodoPontoFactory.fromPeriodo(
                ...value,
            ).getCompetencias({
                diaInicioPonto: empresa.diaInicioPonto,
                trocasInicioPonto: empresa.TrocaInicioPonto,
            })
            setStore(competencia)
        }
    }, [value[1]])

    const handleNext = React.useCallback(() => {
        switch (td) {
            case 'competencia':
                onChange(([oldInicial, oldFinal]) => {
                    const [competencia] = PeriodoPontoFactory.fromPeriodo(
                        oldInicial,
                        oldFinal,
                    ).getCompetencias({
                        diaInicioPonto: empresa.diaInicioPonto,
                        trocasInicioPonto: empresa.TrocaInicioPonto,
                    })
                    const competenciaProxima = dayjs(competencia)
                        .add(1, 'month')
                        .format('YYYY-MM')
                    const [dataInicial, dataFinal] =
                        PeriodoPontoFactory.fromCompetencia(
                            competenciaProxima,
                            {
                                diaInicioPonto: empresa.diaInicioPonto,
                                trocasInicioPonto: empresa.TrocaInicioPonto,
                            },
                        ).getPeriodo()
                    return [
                        dataInicial.format('YYYY-MM-DD'),
                        dataFinal.format('YYYY-MM-DD'),
                    ]
                })
                break
            case 'data':
                onChange(([oldInicial]) => {
                    const diaProximo = dayjs(oldInicial)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaProximo, diaProximo]
                })
                break
            case 'periodo':
                onChange(([oldInicial, oldFinal]) => {
                    const diaProximoInicial = dayjs(oldInicial)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    const diaProximoFinal = dayjs(oldFinal)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaProximoInicial, diaProximoFinal]
                })
                break
        }
    }, [value, onChange, empresa])

    const handlePrev = React.useCallback(() => {
        switch (td) {
            case 'competencia':
                onChange(([oldInicial, oldFinal]) => {
                    const [competencia] = PeriodoPontoFactory.fromPeriodo(
                        oldInicial,
                        oldFinal,
                    ).getCompetencias({
                        diaInicioPonto: empresa.diaInicioPonto,
                        trocasInicioPonto: empresa.TrocaInicioPonto,
                    })
                    const competenciaAnterior = dayjs(competencia)
                        .subtract(1, 'month')
                        .format('YYYY-MM')
                    const [dataInicial, dataFinal] =
                        PeriodoPontoFactory.fromCompetencia(
                            competenciaAnterior,
                            {
                                diaInicioPonto: empresa.diaInicioPonto,
                                trocasInicioPonto: empresa.TrocaInicioPonto,
                            },
                        ).getPeriodo()
                    return [
                        dataInicial.format('YYYY-MM-DD'),
                        dataFinal.format('YYYY-MM-DD'),
                    ]
                })
                break
            case 'data':
                onChange(([oldInicial]) => {
                    const diaAnterior = dayjs(oldInicial)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaAnterior, diaAnterior]
                })
                break
            case 'periodo':
                onChange(([oldInicial, oldFinal]) => {
                    const diaAnteriorInicial = dayjs(oldInicial)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    const diaAnteriorFinal = dayjs(oldFinal)
                        .subtract(1, 'day')
                        .format('YYYY-MM-DD')
                    return [diaAnteriorInicial, diaAnteriorFinal]
                })
                break
        }
    }, [value, onChange])

    return (
        <Context.Provider
            value={{
                state: value,
                handlePrev,
                handleNext,
                handleChange: onChange,
                label,
                type: td,
                changeType: setTd,
                allowedTypes,
                empresa,
            }}
        >
            <div
                className={cn('flex gap-2 items-center', className)}
                style={{
                    width: width ?? 'fit-content',
                }}
            >
                {children}
            </div>
        </Context.Provider>
    )
}

interface IDateSelectorProps {
    /** Campo só de leitura: mostra o valor, mas não abre o seletor. */
    readOnly?: boolean
    disabled?: boolean
    /** Classe do campo em si — largura, altura e afins. */
    className?: string
    /** Classe do wrapper do campo — usada para esticar o campo em formulários. */
    containerClassName?: string
    /**
     * Texto de apoio abaixo do campo. Fica **fora** da caixa do picker para o
     * realce de hover não vazar sobre a linha do helper.
     */
    helperText?: string
    /** Sobrescreve o rótulo derivado do tipo (`Competência`/`Período`/`Data`). */
    label?: string
    variant?: 'filled' | 'outlined'
    size?: 'small' | 'medium'
}

function DateSelector({
    readOnly,
    disabled,
    className,
    containerClassName,
    helperText,
    label: labelProp,
    variant = 'filled',
    size = 'small',
}: IDateSelectorProps) {
    // Sem wrapper quando não há nada pra ele carregar — mantém o DOM das
    // dezenas de usos existentes intacto.
    const comWrapper = !!containerClassName || !!helperText
    const [aberto, setAberto] = useState(false)

    return (
        <Context.Consumer>
            {({
                state: [dataInicial, dataFinal],
                label,
                type,
                changeType,
                empresa,
                handleChange,
                allowedTypes,
            }) => {
                if (!empresa) return null
                const Picker = () => (
                    <div
                        className={cn(
                            'overflow-hidden flex items-end rounded-t-sm',
                            {
                                'pointer-events-none': readOnly || disabled,
                                'cursor-pointer': !readOnly && !disabled,
                                'w-full': comWrapper,
                            },
                        )}
                    >
                        <TextField
                            readOnly
                            disabled={disabled}
                            size={size}
                            variant={variant}
                            unHelperText
                            // Só o `<input>` fica sem ponteiro: a caixa precisa
                            // receber o hover do `inputVariants`.
                            className={cn(
                                '[&_input]:pointer-events-none size-full p-0',
                                className,
                            )}
                            inputClassName={cn(
                                aberto && realceCampoAberto[variant],
                                '[&_[data-slot=input-label]]:capitalize',
                                readOnly
                                    ? '[&_[data-slot=input-label]]:cursor-auto'
                                    : '[&_[data-slot=input-label]]:cursor-pointer',
                            )}
                            label={
                                labelProp ||
                                label ||
                                match(type)
                                    .with('competencia', () => 'Competência')
                                    .with('periodo', () => 'Período')
                                    .with('data', () => 'Data')
                                    .exhaustive()
                            }
                            // O `format` do MUI vira texto montado aqui:
                            // o campo é display, não entrada de dados.
                            value={match(type)
                                .with('competencia', () =>
                                    dayjs(
                                        PeriodoPontoFactory.fromPeriodo(
                                            dataInicial,
                                            dataFinal,
                                        )
                                            .getCompetencias({
                                                diaInicioPonto:
                                                    empresa.diaInicioPonto,
                                                trocasInicioPonto:
                                                    empresa.TrocaInicioPonto,
                                            })
                                            .at(-1),
                                        'YYYY-MM',
                                    )
                                        .locale('pt-br')
                                        .format('MM/YYYY (MMMM)'),
                                )
                                .with('data', () =>
                                    dayjs(dataFinal)
                                        .locale('pt-br')
                                        .format('DD/MM/YYYY (ddd)'),
                                )
                                .with(
                                    'periodo',
                                    () =>
                                        `${dayjs(dataInicial).format(
                                            'DD/MM/YY',
                                        )} a ${dayjs(dataFinal).format(
                                            'DD/MM/YY',
                                        )}`,
                                )
                                .exhaustive()}
                        />
                    </div>
                )

                const picker = readOnly ? (
                    Picker()
                ) : (
                    <Menu.Root open={aberto} onOpenChange={setAberto}>
                        <Menu.Trigger>{Picker()}</Menu.Trigger>
                        <Menu.List className="py-0 w-xs">
                            <Tabs
                                value={type}
                                onValueChange={valor =>
                                    trocarTipo(
                                        valor as Tipo,
                                        dataFinal,
                                        empresa,
                                        handleChange,
                                        changeType,
                                    )
                                }
                                className="gap-0 rounded-none shadow-none dark:bg-transparent"
                            >
                                <div className="border-b">
                                    {allowedTypes.length > 1 && (
                                        <TabsList
                                            variant="underline"
                                            className="w-full px-1"
                                        >
                                            {TIPOS.filter(tipo =>
                                                allowedTypes.includes(tipo),
                                            ).map(tipo => (
                                                <TabsTrigger
                                                    key={tipo}
                                                    value={tipo}
                                                    variant="underline"
                                                    className="flex-1"
                                                >
                                                    {LABEL_TIPO[tipo]}
                                                </TabsTrigger>
                                            ))}
                                        </TabsList>
                                    )}
                                </div>
                                <TabsContent
                                    value="competencia"
                                    className="p-0"
                                >
                                    <SelectCompetencia />
                                </TabsContent>
                                <TabsContent value="data" className="p-0">
                                    <SelectData />
                                </TabsContent>
                                <TabsContent value="periodo" className="p-0">
                                    <SelectPeriodo />
                                </TabsContent>
                            </Tabs>
                        </Menu.List>
                    </Menu.Root>
                )

                if (!comWrapper) return picker
                return (
                    <div
                        className={cn(
                            'flex min-w-0 flex-col',
                            containerClassName,
                        )}
                    >
                        {picker}
                        {helperText && (
                            <p className="ml-3 mt-1 text-xs text-secondary-foreground">
                                {helperText}
                            </p>
                        )}
                    </div>
                )
            }}
        </Context.Consumer>
    )
}

function SelectCompetencia() {
    return (
        <Context.Consumer>
            {({ handleChange, empresa, state: [dataInicial, dataFinal] }) => (
                <div className="size-full">
                    <MonthCalendar
                        className="w-full"
                        key={dayjs(dataFinal).year()}
                        onChange={(competencia: Dayjs) => {
                            const [diaInicial, diaFinal] =
                                PeriodoPontoFactory.fromCompetencia(
                                    competencia.format('YYYY-MM'),
                                    {
                                        diaInicioPonto: empresa.diaInicioPonto,
                                        trocasInicioPonto:
                                            empresa.TrocaInicioPonto,
                                    },
                                ).getPeriodo()
                            handleChange([
                                diaInicial.format('YYYY-MM-DD'),
                                diaFinal.format('YYYY-MM-DD'),
                            ] as TValue)
                        }}
                        value={dayjs(dataFinal)}
                    />
                    <div className="text-center py-4 select-none w-full">
                        Período de{' '}
                        <strong>
                            {dayjs(dataInicial).format('DD/MM/YYYY')}
                        </strong>{' '}
                        a{' '}
                        <strong>{dayjs(dataFinal).format('DD/MM/YYYY')}</strong>
                    </div>
                </div>
            )}
        </Context.Consumer>
    )
}

function SelectData() {
    return (
        <Context.Consumer>
            {({ handleChange, state: [, dataSelecionada] }) => (
                <div className="size-full">
                    <Calendar
                        className="mx-auto"
                        mode="single"
                        // `defaultMonth` só vale na montagem. Mudou o mês do dia
                        // escolhido (clique num dia de fora do mês exibido), o
                        // grid renasce nele — era o que o `DateCalendar` fazia.
                        key={dayjs(dataSelecionada).format('YYYY-MM')}
                        selected={dayjs(dataSelecionada).toDate()}
                        defaultMonth={dayjs(dataSelecionada).toDate()}
                        onSelect={data => {
                            const dia = dayjs(data || undefined).format(
                                'YYYY-MM-DD',
                            )
                            handleChange([dia, dia] as TValue)
                        }}
                    />
                </div>
            )}
        </Context.Consumer>
    )
}

function SelectPeriodo() {
    return (
        <Context.Consumer>
            {({ handleChange, state: [dataInicial, dataFinal] }) => (
                <div className="size-full flex flex-col px-2 py-4">
                    <div className="inline-flex justify-between items-center mb-4">
                        <DateField
                            label="De"
                            value={dayjs(dataInicial)}
                            className="flex-1 mr-2"
                            unHelperText
                            onChange={data => {
                                const dia = dayjs(data || undefined).format(
                                    'YYYY-MM-DD',
                                )
                                handleChange([dia, dataFinal] as TValue)
                            }}
                        />
                        <BotoesNavegacao
                            onAnterior={() =>
                                handleChange([
                                    dayjs(dataInicial)
                                        .subtract(1, 'day')
                                        .format('YYYY-MM-DD'),
                                    dataFinal,
                                ] as TValue)
                            }
                            onProximo={() =>
                                handleChange([
                                    dayjs(dataInicial)
                                        .add(1, 'day')
                                        .format('YYYY-MM-DD'),
                                    dataFinal,
                                ] as TValue)
                            }
                            rotuloAnterior="Dia anterior"
                            rotuloProximo="Próximo dia"
                            size="medium"
                        />
                    </div>
                    <div className="inline-flex justify-between items-center">
                        <DateField
                            label="Até"
                            value={dayjs(dataFinal)}
                            className="flex-1 mr-2"
                            unHelperText
                            onChange={data => {
                                const dia = dayjs(data || undefined).format(
                                    'YYYY-MM-DD',
                                )
                                handleChange([dataInicial, dia] as TValue)
                            }}
                        />
                        <BotoesNavegacao
                            onAnterior={() =>
                                handleChange([
                                    dataInicial,
                                    dayjs(dataFinal)
                                        .subtract(1, 'day')
                                        .format('YYYY-MM-DD'),
                                ] as TValue)
                            }
                            onProximo={() =>
                                handleChange([
                                    dataInicial,
                                    dayjs(dataFinal)
                                        .add(1, 'day')
                                        .format('YYYY-MM-DD'),
                                ] as TValue)
                            }
                            rotuloAnterior="Dia anterior"
                            rotuloProximo="Próximo dia"
                            size="medium"
                        />
                    </div>
                </div>
            )}
        </Context.Consumer>
    )
}

function Buttons({
    className,
    size,
}: Pick<React.ComponentProps<typeof BotoesNavegacao>, 'className' | 'size'>) {
    return (
        <Context.Consumer>
            {({ handlePrev, handleNext }) => (
                <BotoesNavegacao
                    onAnterior={handlePrev}
                    onProximo={handleNext}
                    rotuloAnterior="Data anterior"
                    rotuloProximo="Próxima data"
                    size={size}
                    className={className}
                />
            )}
        </Context.Consumer>
    )
}

interface InputPeriodo {
    /**
     * Tipo de período no render inicial,
     * deve ser o index 0 passado ao allowedTypes do SP.Root.
     * Pode ser ignorado caso defaultValue for informado
     * @default 'competencia'
     * */
    type: Tipo
    /**
     * Quantidade máxima de dias permitidos no período
     * @default 366
     * */
    periodoMax: number
    /** Valor inicial do hook */
    defaultValue?: TValue
}

/**
 * Hook para gerenciar o período de datas, controla datas iniciais e finais
 * @param type Tipo de período no render inicial, deve ser o index 0 passado ao allowedTypes do SP.Root
 * @param periodoMax Quantidade máxima de dias permitidos no período
 * @example const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
 */
function usePeriodo(
    {
        type = 'competencia',
        periodoMax = 366,
        defaultValue = ['', ''],
    }: Partial<InputPeriodo> = {
        type: 'competencia',
        periodoMax: 366,
    },
) {
    const empresa = useStore(state => state.company)
    const competenciaAberta = empresa?.competenciaAtual

    let defaultInicial: string = defaultValue?.[0] || ''
    let defaultFinal: string = defaultValue?.[1] || ''

    const wasPreloadedRef = React.useRef(false)

    function getDefaultValues() {
        if (!empresa) {
            if (type === 'data') {
                const hoje = dayjs().format('YYYY-MM-DD')
                return [hoje, hoje]
            }
            return [
                dayjs().startOf('month').format('YYYY-MM-DD'),
                dayjs().endOf('month').format('YYYY-MM-DD'),
            ]
        }

        let defaultInicialLocal = defaultValue?.[0],
            defaultFinalLocal = defaultValue?.[1]

        switch (type) {
            case 'competencia':
            case 'periodo':
                const [dataInicial, dataFinal] =
                    PeriodoPontoFactory.fromCompetencia(
                        dayjs(competenciaAberta).format('YYYY-MM'),
                        {
                            diaInicioPonto: empresa.diaInicioPonto,
                            trocasInicioPonto: empresa.TrocaInicioPonto,
                        },
                    ).getPeriodo(true)
                defaultInicialLocal = dataInicial
                defaultFinalLocal = dataFinal
                break
            case 'data':
                const hoje = dayjs().format('YYYY-MM-DD')
                defaultInicialLocal = hoje
                defaultFinalLocal = hoje
                break
        }

        return [defaultInicialLocal, defaultFinalLocal]
    }

    if (!defaultInicial || !defaultFinal) {
        // Só chega aqui sem `defaultValue`, e todo chamador assim usa
        // `type: 'competencia'`. Um chamador `type: 'data'` sem `defaultValue`
        // faria a troca de empresa jogar a data para hoje, sem aviso.
        if (!empresa) wasPreloadedRef.current = true
        ;[defaultInicial, defaultFinal] = getDefaultValues()
    }

    const [[dataInicial, dataFinal], _setPeriodo] = useState<TValue>([
        defaultInicial,
        defaultFinal,
    ])

    function setPeriodo(value: TValue | ((prev: TValue) => TValue) | null) {
        if (value === null) return _setPeriodo([defaultInicial, defaultFinal])

        _setPeriodo(prev => {
            const newValue = typeof value === 'function' ? value(prev) : value
            if (Array.isArray(newValue) && newValue.length === 2) {
                const [start, end] = newValue
                if (dayjs.utc(end).diff(dayjs.utc(start), 'day') > periodoMax) {
                    toast.error(
                        `O período não pode ser superior a ${periodoMax} dias`,
                    )
                    return prev
                }
            }
            return newValue
        })
    }

    useEffect(() => {
        if (wasPreloadedRef.current && empresa) {
            wasPreloadedRef.current = false
            setPeriodo(getDefaultValues() as TValue)
            return
        }

        if (empresa && type == 'competencia') {
            const [competencia] = PeriodoPontoFactory.fromPeriodo(
                dataInicial,
                dataFinal,
            ).getCompetencias({
                diaInicioPonto: empresa.diaInicioPonto,
                trocasInicioPonto: empresa.TrocaInicioPonto,
            })
            if (
                empresa.competenciaAtual &&
                competencia != empresa.competenciaAtual
            ) {
                const [diaInicial, diaFinal] =
                    PeriodoPontoFactory.fromCompetencia(
                        dayjs(empresa.competenciaAtual).format('YYYY-MM'),
                        {
                            diaInicioPonto: empresa.diaInicioPonto,
                            trocasInicioPonto: empresa.TrocaInicioPonto,
                        },
                    ).getPeriodo(true)
                setPeriodo([diaInicial, diaFinal])
            }
        }
    }, [empresa?.id])

    return [[dataInicial, dataFinal], setPeriodo] as const
}

export { usePeriodo }

/**
 * @example
 *  import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
 *
 *  const [[dataInicial, dataFinal], setPeriodo] = usePeriodo('competencia')
 *
 *  <SP.Root
 *      onChange={setPeriodo}
 *      value={[dataInicial, dataFinal]}
 *      allowedTypes={['competencia', 'periodo', 'data']}
 *      variant="filled"
 *      size="small"
 *      width={220}
 *  >
 *      <SP.DateSelector />
 *      <SP.Buttons />
 *  </SP.Root>
 */
export default {
    Root,
    DateSelector,
    Buttons,
}
